import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IMAGES } from "../lib/brand";

export default function ActivateAccount() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const nav = useNavigate();
  const { adoptSession } = useAuth();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => { document.title = "Activate your account · Il Salotto Digitale"; }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters."); return; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/activate", { token, password: form.password });
      adoptSession(data);
      setDone(true);
      toast.success("Account activated. Welcome.");
      setTimeout(() => nav("/account", { replace: true }), 1600);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="activate-page" className="min-h-screen pt-20 grid lg:grid-cols-2">
      <div className="hidden lg:block relative overflow-hidden">
        <img src={IMAGES.interior} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-ink/30" />
        <div className="absolute bottom-12 left-12 right-12 text-bg">
          <p className="text-[11px] uppercase tracking-ultra mb-4 text-bg/70">Il Salotto Digitale</p>
          <p className="font-serif text-4xl font-light leading-tight">Your purchase is waiting. Choose a password to open it.</p>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16 lg:py-0">
        <div className="w-full max-w-md">
          {!token ? (
            <div data-testid="activate-invalid">
              <h1 className="font-serif font-light text-4xl text-ink tracking-tight">Invalid activation link</h1>
              <p className="mt-5 text-sm text-ink-soft font-light">This link is missing its token. Open the link from your purchase email, or set a password with Forgot password.</p>
              <Link to="/forgot-password" data-testid="activate-forgot-link" className="mt-10 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] text-ink link-underline">
                Use Forgot password <ArrowRight size={14} />
              </Link>
            </div>
          ) : done ? (
            <div data-testid="activate-success">
              <div className="w-14 h-14 border border-line flex items-center justify-center"><Check size={26} strokeWidth={1.4} className="text-ink" /></div>
              <h1 className="mt-8 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">You're in</h1>
              <p className="mt-5 text-sm text-ink-soft font-light">Your account is active. Taking you to your library…</p>
            </div>
          ) : (
            <>
              <p className="text-[11px] uppercase tracking-ultra text-accent">Il Salotto Digitale</p>
              <h1 className="mt-6 font-serif font-light text-4xl lg:text-5xl text-ink tracking-tight">Activate your account</h1>
              <p className="mt-5 text-sm text-ink-soft font-light">Choose a password for the email address you used at checkout. You'll use it to return to your library.</p>
              <form onSubmit={submit} className="mt-10 space-y-6" data-testid="activate-form">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">Password</label>
                  <input data-testid="activate-password" type="password" required minLength={6} autoComplete="new-password" value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 text-ink focus:outline-none focus:border-ink" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-ink-soft mb-2">Confirm password</label>
                  <input data-testid="activate-confirm" type="password" required minLength={6} autoComplete="new-password" value={form.confirm}
                    onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                    className="w-full border-b border-line bg-transparent py-3 text-ink focus:outline-none focus:border-ink" />
                </div>
                <button data-testid="activate-submit" disabled={loading}
                  className="w-full bg-ink text-bg py-4 text-[11px] uppercase tracking-[0.25em] hover:bg-[#33312E] transition-colors disabled:opacity-60">
                  {loading ? "Activating…" : "Activate and open my library"}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
